<template>
  <div class="tinymce-container-333">
    <editor
      v-model="content"
      :init="editorInit"
    />
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import tinymce from 'tinymce/tinymce'
import 'tinymce/themes/silver'
import 'tinymce/icons/default'

// 1. 引入必要插件
import 'tinymce/plugins/image'
import 'tinymce/plugins/paste'

// 2. 引入皮肤与样式表
import 'tinymce/skins/ui/oxide/skin.min.css'
import 'tinymce/skins/ui/oxide/content.min.css'
import 'tinymce/skins/content/default/content.min.css'

export default {
  name: 'TinymceEditor',
  components: { Editor },
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      content: this.value,
      uploaded_file_list: [], // 存储正文内嵌图片列表
      editorInit: {
        height: 600,
        skin: false,
        content_css: false,
        statusbar: false, // 💡 彻底关掉底部状态栏（去除 p 路径和 Powered by Tiny 标志）
        plugins: 'image paste',
        toolbar: 'undo redo | bold italic | alignleft aligncenter alignright | customImageBtn',

        paste_data_images: true,
        urlconverter_callback: (url) => url,

        // 💡 1. 核心 setup：自定义选图按钮 + 智能拦截非图片文件（实现 Gmail 体验）
        setup: (editor) => {
          // 重写图片按钮：点击直接弹出系统 Choose File 界面
          editor.ui.registry.addButton('customImageBtn', {
            icon: 'image',
            tooltip: '上传图片',
            onAction: () => {
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', 'image/*')
              
              input.onchange = async () => {
                const file = input.files[0]
                if (!file) return

                const formData = new FormData()
                formData.append('file', file, file.name)

                try {
                  const response = await this.$http.post(
                    '/v1/pub/helpers/upload-file-seller?dir_tag=goods&token=' + (localStorage.getItem('token') || ''),
                    formData
                  )
                  const res = response.data
                  if (res && res.code === 1 && res.data && res.data.file_oss) {
                    this.collectFileData(res.data)
                    const ossUrl = res.data.file_oss
                    // 插入图片并加上防爆屏样式
                    editor.insertContent(`<img src="${ossUrl}" style="max-width:100%; max-height:366px; object-fit:contain;" />`)
                  }
                } catch (err) {
                  console.error('图片上传失败:', err)
                }
              }

              input.click()
            }
          })

          // 拦截 paste 和 drop：非图片文件自动路由到底部附件区
          editor.on('paste drop', (e) => {
            const clipboardData = e.clipboardData || (e.dataTransfer && e.dataTransfer)
            if (!clipboardData || !clipboardData.files || clipboardData.files.length === 0) return

            const files = Array.from(clipboardData.files)
            // 筛选出非图片文件 (如 PDF, ZIP, DOCX, CSV 等)
            const nonImageFiles = files.filter(file => !file.type.startsWith('image/'))

            if (nonImageFiles.length > 0) {
              e.preventDefault()

              nonImageFiles.forEach(async (file) => {
                const formData = new FormData()
                formData.append('file', file, file.name)
                    this.$emit('set_upload_loading', true)

                try {
                  const response = await this.$http.post(
                    '/v1/pub/helpers/upload-file-seller?dir_tag=attachment&token=' + (localStorage.getItem('token') || ''),
                    formData
                  )
                  this.$emit('set_upload_loading', false)
                  const res = response.data
                  if (res && res.code === 1 && res.data) {
                    this.$emit('add_attachment', res.data)
                  }
                } catch (err) {
                  this.$emit('set_upload_loading', false)
                  console.error('附件上传失败:', err)
                }
              })
            }
          })
        },

        // 💡 2. 处理粘贴第三方外链图片的抓取转存
        paste_postprocess: (plugin, args) => {
          const imgs = args.node.querySelectorAll('img')
          imgs.forEach(img => {
            img.style.maxWidth = '100%'
            img.style.maxHeight = '300px'
            img.style.objectFit = 'contain'

            const src = img.src || ''

            if (src.startsWith('data:') || src.startsWith('blob:')) {
              return
            }

            if (src.startsWith('http') && !src.includes('casediy-sh.oss')) {
              const originalSrc = src

              this.uploadExternalImageByUrl(originalSrc).then(ossUrl => {
                if (ossUrl && ossUrl !== originalSrc) {
                  this.$nextTick(() => {
                    const activeEditor = tinymce.activeEditor
                    if (activeEditor) {
                      const editorBody = activeEditor.getBody()
                      const targetImgs = editorBody.querySelectorAll(`img[src="${originalSrc}"]`)
                      targetImgs.forEach(targetImg => {
                        targetImg.src = ossUrl
                      })
                      this.content = activeEditor.getContent()
                      this.$emit('input', this.content)
                    }
                  })
                }
              })
            }
          })
        },

        // 💡 3. 处理截图 / 拖拽图片上传
        images_upload_handler: async (blobInfo, success, failure) => {
          const formData = new FormData()
          formData.append('file', blobInfo.blob(), blobInfo.filename())

          try {
            const response = await this.$http.post(
              '/v1/pub/helpers/upload-file-seller?dir_tag=goods&token=' + (localStorage.getItem('token') || ''),
              formData
            )

            const res = response.data
            if (res && res.code === 1 && res.data && res.data.file_oss) {
              this.collectFileData(res.data)
              const ossUrl = res.data.file_oss
              success(ossUrl)

              setTimeout(() => {
                const activeEditor = tinymce.activeEditor
                if (activeEditor) {
                  const editorBody = activeEditor.getBody()
                  const targetImgs = editorBody.querySelectorAll(`img[src="${ossUrl}"]`)
                  targetImgs.forEach(img => {
                    img.style.maxWidth = '100%'
                    img.style.maxHeight = '300px'
                    img.style.objectFit = 'contain'
                  })
                }
              }, 50)
            } else {
              failure((res && res.msg) || '上传失败')
            }
          } catch (err) {
            failure('网络请求异常')
          }
        }
      }
    }
  },
  watch: {
    content(val) {
      this.$emit('input', val)
    },
    value(val) {
      if (val !== this.content) {
        this.content = val
      }
    }
  },
  methods: {
    // 收集图片元数据
    collectFileData(fileData) {
      if (!fileData || !fileData.file_oss) return
      const exists = this.uploaded_file_list.some(item => item.file_oss === fileData.file_oss)
      if (!exists) {
        this.uploaded_file_list.push(fileData)
      }
    },

    // 转存第三方网络图片
    async uploadExternalImageByUrl(imgUrl) {
      try {
        const params = {
          url: imgUrl,
          dir_tag: 'editor'
        }
        const response = await this.$http.post(
          '/v1/pub/helpers/upload-file-by-url-seller?token=' + (localStorage.getItem('token') || ''),
          params
        )

        const res = response.data
        if (res && res.code === 1 && res.data && res.data.file_oss) {
          this.collectFileData(res.data)
          return res.data.file_oss
        }
      } catch (e) {
        console.warn('通过 URL 转存外链图片失败，保留原始地址:', e)
      }
      return imgUrl
    },

    // 💡 供父组件直接调用 this.$refs.xxx.getFileList() 拿到留在文本框内的有效图片列表
    getFileList() {
      if (!this.content) return []
      return this.uploaded_file_list.filter(item => this.content.includes(item.file_oss))
    }
  }
}
</script>

<style>
.tinymce-container-333 {
  width: 100%;
}
.tinymce-container-333 .w-e-text-container{
  padding: 10px;
  box-sizing: border-box;
}
</style>


// WEBPACK FOOTER //
// src/pages/public/_TinymceEditor.vue